import * as THREE from 'three'
import { STATUS_STYLES } from '../api/topology'
import { NODE_MODEL_ASSETS } from './modelLibrary'
export { NODE_MODEL_ASSETS } from './modelLibrary'

// Procedural Three.js models for the topology node categories. Every builder
// fills in a ModelBuilder so the graph can recolor and highlight the parts.

const statusColor = (status) => (STATUS_STYLES[status] || STATUS_STYLES.normal).color

const PANEL_COLOR = '#263548'
const PORT_COLOR = '#111827'
const CAVITY_COLOR = '#030712'
const GLASS_COLOR = '#0b1220'
const SHELL_COLOR = '#e2e8f0'

// Real products are assemblies, not single-colour sculptures. These restrained
// category palettes mimic common production finishes while keeping node status
// colours reserved for LEDs and the ground ring.
const DEVICE_FINISHES = {
  router: { body: '#252c31', panel: '#0c1218', plastic: '#151a1e', trim: '#626d75', accent: '#2e8c93' },
  switch: { body: '#30373c', panel: '#11171d', plastic: '#0f1419', trim: '#747f87', accent: '#347eaa' },
  station: { body: '#abb4bb', panel: '#59656f', plastic: '#d6dadd', trim: '#8d979f', accent: '#c5912e' },
  satellite: { body: '#aeb6bd', panel: '#515d68', plastic: '#dfe2e3', trim: '#aeb7bd', accent: '#b18a3b' },
  drone: { body: '#252b30', panel: '#13181d', plastic: '#1a1f23', trim: '#687179', accent: '#c57427' },
  terminal: { body: '#343c42', panel: '#1d252c', plastic: '#171c21', trim: '#758088', accent: '#487fa4' },
  gateway: { body: '#343b40', panel: '#0e151c', plastic: '#141a1e', trim: '#778189', accent: '#a73b3d' },
  service: { body: '#30383e', panel: '#121a21', plastic: '#10161a', trim: '#707b83', accent: '#397e9e' },
  storage: { body: '#353d43', panel: '#0f171e', plastic: '#141a1e', trim: '#7c858c', accent: '#517c90' },
  controller: { body: '#394146', panel: '#141b21', plastic: '#14191d', trim: '#778188', accent: '#c57a2e' },
  sensor: { body: '#aeb7bd', panel: '#59636b', plastic: '#d9dddf', trim: '#929da4', accent: '#498c87' },
  actuator: { body: '#31556d', panel: '#29343d', plastic: '#151b20', trim: '#a2a9ae', accent: '#bd712d' }
}

const DEFAULT_FINISH = { body: '#46515a', panel: PANEL_COLOR, plastic: '#171d22', trim: '#949da5', accent: '#3370ff' }

// Small, shared procedural surface maps keep the equipment from looking like
// perfectly smooth CAD plastic. They are deterministic, lightweight and need
// no external image files, so every model receives the same real-world finish.
const surfaceTextureCache = new Map()

function surfaceNoise(seed) {
  let state = seed >>> 0
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    return state / 4294967296
  }
}

export function makeSurfaceMaps(kind) {
  if (surfaceTextureCache.has(kind)) return surfaceTextureCache.get(kind)
  const size = 128
  const colorCanvas = document.createElement('canvas')
  const roughnessCanvas = document.createElement('canvas')
  const normalCanvas = document.createElement('canvas')
  ;[colorCanvas, roughnessCanvas, normalCanvas].forEach((canvas) => {
    canvas.width = size
    canvas.height = size
  })
  const colorContext = colorCanvas.getContext('2d')
  const roughnessContext = roughnessCanvas.getContext('2d')
  const normalContext = normalCanvas.getContext('2d')
  const colorImage = colorContext.createImageData(size, size)
  const roughnessImage = roughnessContext.createImageData(size, size)
  const normalImage = normalContext.createImageData(size, size)
  const height = new Float32Array(size * size)
  const random = surfaceNoise({ powder: 7127, brushed: 3911, rubber: 8513, ceramic: 2357, carbon: 6199 }[kind] || 127)

  for (let y = 0; y < size; y++) {
    const brushedLine = Math.sin(y * 0.72) * 0.42 + Math.sin(y * 2.37) * 0.18
    for (let x = 0; x < size; x++) {
      const index = y * size + x
      const grain = random() - 0.5
      const coarse = Math.sin(x * 0.16 + y * 0.11) * 0.16
      let relief = grain * 0.7 + coarse
      let shade = 240 + grain * 18 + coarse * 16
      let roughness = 220 + grain * 30 + coarse * 18
      if (kind === 'brushed') {
        relief = brushedLine * 0.72 + grain * 0.16
        shade = 236 + brushedLine * 16 + grain * 6
        roughness = 180 + brushedLine * 38 + grain * 16
      } else if (kind === 'rubber') {
        const stipple = Math.sin(x * 0.44) * Math.sin(y * 0.39)
        relief = grain * 0.9 + stipple * 0.35
        shade = 232 + grain * 22 + stipple * 7
        roughness = 242 + grain * 10
      } else if (kind === 'ceramic') {
        relief = grain * 0.32 + coarse * 0.25
        shade = 246 + grain * 11 + coarse * 5
        roughness = 205 + grain * 20
      } else if (kind === 'carbon') {
        const weaveA = Math.sin((x + y) * 0.78)
        const weaveB = Math.sin((x - y) * 0.78)
        const weave = weaveA * weaveB
        relief = weave * 0.72 + grain * 0.12
        shade = 218 + weave * 27 + grain * 5
        roughness = 178 - weave * 24 + grain * 12
      }
      height[index] = relief
      const pixel = index * 4
      const value = THREE.MathUtils.clamp(Math.round(shade), 0, 255)
      const rough = THREE.MathUtils.clamp(Math.round(roughness), 0, 255)
      colorImage.data.set([value, value, value, 255], pixel)
      roughnessImage.data.set([rough, rough, rough, 255], pixel)
    }
  }

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const index = y * size + x
      const left = height[y * size + (x + size - 1) % size]
      const right = height[y * size + (x + 1) % size]
      const up = height[((y + size - 1) % size) * size + x]
      const down = height[((y + 1) % size) * size + x]
      const strength = kind === 'rubber' ? 28 : kind === 'powder' ? 24 : kind === 'brushed' ? 16 : kind === 'carbon' ? 20 : 11
      const pixel = index * 4
      normalImage.data.set([
        THREE.MathUtils.clamp(Math.round(128 + (left - right) * strength), 0, 255),
        THREE.MathUtils.clamp(Math.round(128 + (up - down) * strength), 0, 255),
        255,
        255
      ], pixel)
    }
  }

  colorContext.putImageData(colorImage, 0, 0)
  roughnessContext.putImageData(roughnessImage, 0, 0)
  normalContext.putImageData(normalImage, 0, 0)
  const configure = (canvas, color = false) => {
    const texture = new THREE.CanvasTexture(canvas)
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping
    const repeats = kind === 'brushed' ? [1, 2.25] : kind === 'carbon' ? [2.4, 2.4] : [1.35, 1.35]
    texture.repeat.set(...repeats)
    texture.anisotropy = 4
    texture.userData.shared = true
    texture.userData.surfaceDetail = true
    if (color) texture.colorSpace = THREE.SRGBColorSpace
    return texture
  }
  const maps = {
    map: configure(colorCanvas, true),
    roughnessMap: configure(roughnessCanvas),
    normalMap: configure(normalCanvas)
  }
  surfaceTextureCache.set(kind, maps)
  return maps
}

function roundedRectShape(width, depth, radius) {
  const halfWidth = width / 2
  const halfDepth = depth / 2
  const corner = Math.min(radius, halfWidth, halfDepth)
  const shape = new THREE.Shape()
  shape.moveTo(-halfWidth + corner, -halfDepth)
  shape.lineTo(halfWidth - corner, -halfDepth)
  shape.quadraticCurveTo(halfWidth, -halfDepth, halfWidth, -halfDepth + corner)
  shape.lineTo(halfWidth, halfDepth - corner)
  shape.quadraticCurveTo(halfWidth, halfDepth, halfWidth - corner, halfDepth)
  shape.lineTo(-halfWidth + corner, halfDepth)
  shape.quadraticCurveTo(-halfWidth, halfDepth, -halfWidth, halfDepth - corner)
  shape.lineTo(-halfWidth, -halfDepth + corner)
  shape.quadraticCurveTo(-halfWidth, -halfDepth, -halfWidth + corner, -halfDepth)
  return shape
}

// Width along x, depth along z, height along y, centered on the origin.
function roundedBoxGeometry(width, depth, height, radius = 1.4) {
  const bevel = Math.min(0.38, radius * 0.35, height * 0.2)
  const geometry = new THREE.ExtrudeGeometry(roundedRectShape(width, depth, radius), {
    depth: Math.max(0.1, height - bevel * 2),
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 2,
    curveSegments: 8
  })
  geometry.rotateX(-Math.PI / 2)
  geometry.translate(0, -height / 2, 0)
  // ExtrudeGeometry UVs are expressed in shape units. Normalise them so a
  // texture appears once across a chassis instead of repeating dozens of
  // times and visually averaging back into a flat colour.
  const uv = geometry.getAttribute('uv')
  if (uv) {
    let minU = Infinity
    let minV = Infinity
    let maxU = -Infinity
    let maxV = -Infinity
    for (let index = 0; index < uv.count; index++) {
      minU = Math.min(minU, uv.getX(index))
      minV = Math.min(minV, uv.getY(index))
      maxU = Math.max(maxU, uv.getX(index))
      maxV = Math.max(maxV, uv.getY(index))
    }
    const rangeU = Math.max(0.0001, maxU - minU)
    const rangeV = Math.max(0.0001, maxV - minV)
    for (let index = 0; index < uv.count; index++) {
      uv.setXY(index, (uv.getX(index) - minU) / rangeU, (uv.getY(index) - minV) / rangeV)
    }
    uv.needsUpdate = true
  }
  geometry.computeVertexNormals()
  return geometry
}

function shieldGeometry(width, height, depth) {
  const halfWidth = width / 2
  const halfHeight = height / 2
  const shape = new THREE.Shape()
  shape.moveTo(0, halfHeight)
  shape.lineTo(halfWidth, halfHeight * 0.5)
  shape.quadraticCurveTo(halfWidth, halfHeight * 0.05, halfWidth * 0.7, -halfHeight * 0.2)
  shape.quadraticCurveTo(halfWidth * 0.4, -halfHeight * 0.6, 0, -halfHeight)
  shape.quadraticCurveTo(-halfWidth * 0.4, -halfHeight * 0.6, -halfWidth * 0.7, -halfHeight * 0.2)
  shape.quadraticCurveTo(-halfWidth, halfHeight * 0.05, -halfWidth, halfHeight * 0.5)
  shape.closePath()
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelThickness: depth * 0.3,
    bevelSize: depth * 0.35,
    bevelSegments: 2,
    curveSegments: 10
  })
  geometry.computeVertexNormals()
  return geometry
}

// Flat transforms: [x, y, z, rotationX?, rotationY?, rotationZ?].
function instances(geometry, material, transforms) {
  const mesh = new THREE.InstancedMesh(geometry, material, transforms.length)
  const matrix = new THREE.Matrix4()
  const quaternion = new THREE.Quaternion()
  const euler = new THREE.Euler()
  const position = new THREE.Vector3()
  const scale = new THREE.Vector3(1, 1, 1)
  transforms.forEach(([x, y, z, rx = 0, ry = 0, rz = 0], index) => {
    position.set(x, y, z)
    euler.set(rx, ry, rz)
    matrix.compose(position, quaternion.setFromEuler(euler), scale)
    mesh.setMatrixAt(index, matrix)
  })
  mesh.instanceMatrix.needsUpdate = true
  return mesh
}

// Points spread evenly around a circle, used for fins, bolts and antenna rings.
function around(count, radius, y, startAngle = 0) {
  return Array.from({ length: count }, (_, index) => {
    const angle = startAngle + index * Math.PI * 2 / count
    return [Math.cos(angle) * radius, y, Math.sin(angle) * radius, 0, -angle, 0]
  })
}

class ModelBuilder {
  constructor(visual, status, random, category) {
    this.group = new THREE.Group()
    this.materials = []
    this.random = random || (() => 0.5)
    const style = (visual && visual.style) || {}
    const finish = DEVICE_FINISHES[category] || DEFAULT_FINISH
    const powder = makeSurfaceMaps('powder')
    const brushed = makeSurfaceMaps('brushed')
    const rubber = makeSurfaceMaps('rubber')
    const ceramic = makeSurfaceMaps('ceramic')
    const carbon = makeSurfaceMaps('carbon')
    // Painted metal, anodised trim and glass need different reflective layers.
    // MeshPhysicalMaterial is only used where its clear-coat model adds visible value;
    // small dark parts stay on the cheaper MeshStandardMaterial shader.
    this.body = this.material({
      color: finish.body,
      map: powder.map,
      roughnessMap: powder.roughnessMap,
      normalMap: powder.normalMap,
      normalScale: new THREE.Vector2(0.34, 0.34),
      roughness: 0.58,
      metalness: 0.08,
      clearcoat: 0.08,
      clearcoatRoughness: 0.55,
      envMapIntensity: 0.82
    }, true)
    this.panel = this.material({
      color: finish.panel,
      map: brushed.map,
      roughnessMap: brushed.roughnessMap,
      normalMap: brushed.normalMap,
      normalScale: new THREE.Vector2(0.24, 0.24),
      roughness: 0.48,
      metalness: 0.64,
      envMapIntensity: 0.95
    })
    this.port = this.material({ color: PORT_COLOR, roughness: 0.72, metalness: 0.08, envMapIntensity: 0.65 })
    this.cavity = this.material({ color: CAVITY_COLOR, roughness: 0.9, metalness: 0.02, envMapIntensity: 0.45 })
    this.plastic = this.material({
      color: finish.plastic,
      map: rubber.map,
      roughnessMap: rubber.roughnessMap,
      normalMap: rubber.normalMap,
      normalScale: new THREE.Vector2(0.18, 0.18),
      roughness: 0.72,
      metalness: 0.02,
      clearcoat: 0.04,
      clearcoatRoughness: 0.7,
      envMapIntensity: 0.55
    }, true)
    this.accent = this.material({
      color: finish.accent || style.stroke || '#3370FF',
      roughness: 0.36,
      metalness: 0.38,
      clearcoat: 0.16,
      clearcoatRoughness: 0.26,
      anisotropy: 0.22,
      envMapIntensity: 1.05
    }, true)
    this.glass = this.material({
      color: GLASS_COLOR,
      roughness: 0.08,
      metalness: 0.08,
      clearcoat: 1,
      clearcoatRoughness: 0.06,
      reflectivity: 0.82,
      envMapIntensity: 1.35
    }, true)
    this.screen = this.material({
      color: '#07131b',
      emissive: '#0b6b88',
      emissiveIntensity: 0.28,
      roughness: 0.08,
      metalness: 0.04,
      clearcoat: 1,
      clearcoatRoughness: 0.05,
      envMapIntensity: 1.25
    }, true)
    this.screenInk = this.material({
      color: '#8ae8ff',
      emissive: '#18bce8',
      emissiveIntensity: 1.15,
      roughness: 0.3,
      metalness: 0,
      envMapIntensity: 0.25
    })
    this.metal = this.material({
      color: finish.trim,
      map: brushed.map,
      roughnessMap: brushed.roughnessMap,
      normalMap: brushed.normalMap,
      normalScale: new THREE.Vector2(0.2, 0.2),
      roughness: 0.38,
      metalness: 0.92,
      anisotropy: 0.62,
      anisotropyRotation: Math.PI / 2,
      envMapIntensity: 1.12
    }, true)
    this.carbon = this.material({
      color: '#252c32',
      map: carbon.map,
      roughnessMap: carbon.roughnessMap,
      normalMap: carbon.normalMap,
      normalScale: new THREE.Vector2(0.3, 0.3),
      roughness: 0.48,
      metalness: 0.16,
      clearcoat: 0.22,
      clearcoatRoughness: 0.34,
      envMapIntensity: 0.8
    }, true)
    this.rubber = this.material({
      color: '#111418',
      map: rubber.map,
      roughnessMap: rubber.roughnessMap,
      normalMap: rubber.normalMap,
      normalScale: new THREE.Vector2(0.46, 0.46),
      roughness: 0.98,
      metalness: 0,
      envMapIntensity: 0.25
    })
    this.solar = this.material({
      color: '#071d3a',
      roughness: 0.2,
      metalness: 0.18,
      clearcoat: 0.82,
      clearcoatRoughness: 0.12,
      envMapIntensity: 1.35
    }, true)
    this.goldFoil = this.material({
      color: '#b68a38',
      map: powder.map,
      roughnessMap: powder.roughnessMap,
      normalMap: powder.normalMap,
      normalScale: new THREE.Vector2(0.38, 0.38),
      roughness: 0.52,
      metalness: 0.68,
      clearcoat: 0.08,
      clearcoatRoughness: 0.58,
      envMapIntensity: 1.05
    }, true)
    this.shell = this.material({
      color: SHELL_COLOR,
      map: ceramic.map,
      roughnessMap: ceramic.roughnessMap,
      normalMap: ceramic.normalMap,
      normalScale: new THREE.Vector2(0.13, 0.13),
      roughness: 0.46,
      metalness: 0.12,
      clearcoat: 0.16,
      clearcoatRoughness: 0.42,
      envMapIntensity: 0.88,
      side: THREE.DoubleSide
    }, true)
    this.concrete = this.material({
      color: '#969b9d',
      map: powder.map,
      roughnessMap: powder.roughnessMap,
      normalMap: powder.normalMap,
      normalScale: new THREE.Vector2(0.62, 0.62),
      roughness: 1,
      metalness: 0,
      envMapIntensity: 0.28
    })
    const tone = statusColor(status)
    this.indicator = this.material({
      color: tone,
      emissive: tone,
      emissiveIntensity: 0.9,
      roughness: 0.2,
      metalness: 0.1,
      side: THREE.DoubleSide
    })
    this.indicator.userData.baseEmissive = this.indicator.emissive.getHex()
    this.indicator.userData.baseEmissiveIntensity = this.indicator.emissiveIntensity
  }
  material(options, physical = false) {
    const material = physical
      ? new THREE.MeshPhysicalMaterial(options)
      : new THREE.MeshStandardMaterial(options)
    this.materials.push(material)
    if (material.emissive && material.emissive.getHex() !== 0) {
      material.userData.baseEmissive = material.emissive.getHex()
      material.userData.baseEmissiveIntensity = material.emissiveIntensity
    }
    return material
  }
  add(mesh) {
    if (mesh.material) this.materials.push(mesh.material)
    this.group.add(mesh)
    return mesh
  }
  texture() {
    return {
      group: this.group,
      materials: [...new Set(this.materials)],
      indicator: this.indicator
    }
  }
  // 仅保留给未启用的备选模型；当前固定型号不会调用尺寸抖动。
  jitter(base, ratio = 0.12) {
    return base * (1 + (this.random() - 0.5) * 2 * ratio)
  }
}

function addCaseHardware(builder, category, primary) {
  if (!primary?.geometry || !['router', 'switch', 'satellite', 'gateway', 'storage', 'controller'].includes(category)) return
  primary.geometry.computeBoundingBox()
  const box = primary.geometry.boundingBox
  if (!box) return

  const width = box.max.x - box.min.x
  const height = box.max.y - box.min.y
  const screwRadius = THREE.MathUtils.clamp(Math.min(width, height) * 0.035, 0.28, 0.48)
  const insetX = Math.min(1.7, width * 0.12)
  const insetY = Math.min(1.5, height * 0.18)
  const screws = instances(
    new THREE.CylinderGeometry(screwRadius, screwRadius, 0.28, 12),
    builder.metal,
    [
      [box.min.x + insetX, box.min.y + insetY, box.max.z + 0.16, Math.PI / 2],
      [box.max.x - insetX, box.min.y + insetY, box.max.z + 0.16, Math.PI / 2],
      [box.min.x + insetX, box.max.y - insetY, box.max.z + 0.16, Math.PI / 2],
      [box.max.x - insetX, box.max.y - insetY, box.max.z + 0.16, Math.PI / 2]
    ]
  )
  primary.add(screws)

  const outline = new THREE.LineSegments(
    new THREE.EdgesGeometry(primary.geometry, 38),
    new THREE.LineBasicMaterial({ color: '#070a0d', transparent: true, opacity: 0.24, toneMapped: false })
  )
  outline.scale.setScalar(1.002)
  primary.add(outline)
}

// Router: chassis, vents, port bank, status LEDs, badge and two antennae.
function router(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(36, 22, 8, 2.2), b.body))

  b.add(new THREE.Mesh(roundedBoxGeometry(31, 17, 1.1, 1.2), b.plastic)).position.y = 4.35
  b.add(new THREE.Mesh(roundedBoxGeometry(33, 1, 6.2, 1), b.panel)).position.set(0, -0.65, 11.05)

  b.add(instances(
    new THREE.BoxGeometry(12, 0.28, 0.48),
    b.cavity,
    Array.from({ length: 6 }, (_, i) => [0, 4.98, -5.4 + i * 1.75])
  ))

  const portPositions = [-10.2, -5.1, 0, 5.1, 10.2]
  b.add(instances(
    new THREE.BoxGeometry(4.15, 3, 0.95),
    b.port,
    portPositions.map((x) => [x, -1.45, 11.72])
  ))
  b.add(instances(
    new THREE.BoxGeometry(2.75, 1.45, 0.22),
    b.cavity,
    portPositions.map((x) => [x, -1.45, 12.24])
  ))
  b.add(instances(
    new THREE.BoxGeometry(0.72, 0.72, 0.48),
    b.indicator,
    [-2.2, 0, 2.2].map((x) => [x, 1.35, 11.72])
  ))

  const badge = b.add(new THREE.Mesh(new THREE.TorusGeometry(2.9, 0.38, 10, 28), b.accent))
  badge.position.set(0, 5.02, 3.8)
  badge.rotation.x = Math.PI / 2

  ;[-13.2, -5.1, 5.1, 13.2].forEach((x, index) => {
    const antenna = new THREE.Group()
    antenna.position.set(x, 4.1, -8.6)
    const outer = index === 0 || index === 3
    antenna.rotation.set(-0.28, 0, outer ? (index === 0 ? 0.22 : -0.22) : (index === 1 ? 0.06 : -0.06))
    const hinge = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 1.5, 12), b.metal))
    const rod = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 18, 12), b.rubber))
    rod.position.y = 9.35
    const tip = b.add(new THREE.Mesh(new THREE.SphereGeometry(1.12, 12, 8), b.accent))
    tip.position.y = 18.45
    antenna.add(hinge, rod, tip)
    b.group.add(antenna)
  })

  b.add(instances(
    new THREE.CylinderGeometry(1.15, 1.15, 0.8, 12),
    b.rubber,
    [-12.5, 12.5].flatMap((x) => [-7.2, 7.2].map((z) => [x, -4.2, z]))
  ))

  return body
}

// Switch: rack ears, two rows of access ports, per-port LEDs and a console port.
function accessSwitch(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(38, 17, 6.6, 1.6), b.body))

  b.add(new THREE.Mesh(roundedBoxGeometry(34, 13, 0.9, 1), b.body)).position.y = 3.6
  b.add(instances(
    new THREE.BoxGeometry(14, 0.26, 0.42),
    b.cavity,
    Array.from({ length: 7 }, (_, i) => [0, 4.05, -4.5 + i * 1.5])
  ))

  const columns = Array.from({ length: 12 }, (_, i) => -15.4 + i * 2.8)
  const rows = [-1.6, 1.6]
  const portTransforms = rows.flatMap((y) => columns.map((x) => [x, y, 8.7]))
  b.add(instances(new THREE.BoxGeometry(2.3, 2.3, 0.9), b.port, portTransforms))
  b.add(instances(new THREE.BoxGeometry(1.5, 1.5, 0.3), b.cavity, portTransforms.map(([x, y]) => [x, y, 9.2])))
  b.add(instances(
    new THREE.BoxGeometry(1.1, 1.1, 0.5),
    b.indicator,
    columns.map((x) => [x, -3.3, 8.7])
  ))

  ;[-20.6, 20.6].forEach((x) => {
    const ear = b.add(new THREE.Mesh(roundedBoxGeometry(3.4, 12, 7.4, 0.8), b.metal))
    ear.position.set(x, 0, 0)
  })

  const console1 = b.add(new THREE.Mesh(roundedBoxGeometry(3.4, 3.4, 1, 0.5), b.cavity))
  console1.position.set(17.6, -2.1, 8.8)
  const management = b.add(new THREE.Mesh(roundedBoxGeometry(3.4, 3.4, 1, 0.5), b.cavity))
  management.position.set(17.6, 1.1, 8.8)
  const label = b.add(new THREE.Mesh(roundedBoxGeometry(6, 0.6, 2.4, 0.4), b.accent))
  label.position.set(-18.9, 1.6, 8.8)

  return body
}

// Base station: concrete pad, equipment cabinet, mast and three sector antennae.
function station(b) {
  const pad = b.add(new THREE.Mesh(roundedBoxGeometry(30, 26, 2.6, 0.8), b.concrete))
  pad.position.y = -16.5

  const cabinet = b.add(new THREE.Mesh(roundedBoxGeometry(16, 12, 10, 1.2), b.body))
  cabinet.position.set(-10.5, -9.8, 0)
  const cabinetFace = b.add(new THREE.Mesh(roundedBoxGeometry(11.5, 1, 6, 0.6), b.plastic))
  cabinetFace.position.set(-10.5, -10.2, 6.1)
  const cabinetLed = b.add(new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.6, 0.7), b.indicator))
  cabinetLed.position.set(-10.5, -6.4, 6.6)

  const mast = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.6, 2.2, 24, 16), b.metal))
  mast.position.set(7, -3, 0)
  const collar = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2.5, 2.5, 1.6, 16), b.accent))
  collar.position.set(7, 8.2, 0)
  const platform = b.add(new THREE.Mesh(new THREE.CylinderGeometry(6.6, 6.6, 1.4, 22), b.metal))
  platform.position.set(7, 9.6, 0)

  for (let i = 0; i < 3; i++) {
    const angle = Math.PI / 6 + i * Math.PI * 2 / 3
    const panel = b.add(new THREE.Mesh(roundedBoxGeometry(5, 1.8, 13, 0.9), b.shell))
    panel.position.set(7 + Math.cos(angle) * 7.4, 15.5, Math.sin(angle) * 7.4)
    panel.rotation.y = -angle
    const brace = b.add(new THREE.Mesh(roundedBoxGeometry(7.4, 1.1, 1.1, 0.4), b.metal))
    brace.position.set(7 + Math.cos(angle) * 4.2, 10.6, Math.sin(angle) * 4.2)
    brace.rotation.y = -angle
  }

  const rod = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 8, 10), b.accent))
  rod.position.set(7, 25.5, 0)
  const beacon = b.add(new THREE.Mesh(new THREE.SphereGeometry(1.3, 14, 10), b.indicator))
  beacon.position.set(7, 30.2, 0)

  return pad
}

// Satellite: bus, two solar wings and a steerable dish.
function satellite(b) {
  const bus = b.add(new THREE.Mesh(roundedBoxGeometry(13, 13, 12, 1.6), b.shell))
  b.add(new THREE.Mesh(roundedBoxGeometry(13.6, 13.6, 2.2, 0.8), b.goldFoil)).position.y = 0

  ;[-1, 1].forEach((side) => {
    const boom = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 13, 10), b.accent))
    boom.position.set(side * 13, 0, 0)
    boom.rotation.z = Math.PI / 2
    const wing = b.add(new THREE.Mesh(roundedBoxGeometry(19, 12, 1.2, 0.6), b.solar))
    wing.position.set(side * 29, 0, 0)
    const spine = b.add(new THREE.Mesh(roundedBoxGeometry(19.4, 1, 1.4, 0.5), b.accent))
    spine.position.set(side * 29, 0, 0)
    ;[-4, 4].forEach((offset) => {
      const rib = b.add(new THREE.Mesh(roundedBoxGeometry(19.2, 0.8, 0.6, 0.3), b.metal))
      rib.position.set(side * 29, 0.7, offset)
    })
    ;[-6.3, 0, 6.3].forEach((offset) => {
      const cellDivider = b.add(new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.16, 11.2), b.metal))
      cellDivider.position.set(side * 29 + offset, 0.72, 0)
    })
  })

  // Parabolic dish: a lathed profile plus a feed horn at the focus.
  const focus = 4
  const profile = Array.from({ length: 11 }, (_, i) => {
    const radius = Math.max(0.05, i / 10 * 8.2)
    return new THREE.Vector2(radius, radius * radius / (4 * focus))
  })
  const dishGroup = new THREE.Group()
  const bowl = b.add(new THREE.Mesh(new THREE.LatheGeometry(profile, 28), b.shell))
  const feedRod = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, focus, 8), b.accent))
  feedRod.position.y = focus / 2
  const feedHorn = b.add(new THREE.Mesh(new THREE.ConeGeometry(1.15, 1.7, 12), b.accent))
  feedHorn.position.y = focus + 0.6
  dishGroup.add(bowl, feedRod, feedHorn)
  dishGroup.position.set(0, -8, 3.4)
  dishGroup.rotation.x = Math.PI * 0.56
  b.group.add(dishGroup)

  const mast = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 9, 8), b.metal))
  mast.position.set(0, 10.4, 0)
  const tip = b.add(new THREE.Mesh(new THREE.SphereGeometry(1.2, 12, 10), b.indicator))
  tip.position.set(0, 15.2, 0)
  b.add(instances(
    new THREE.BoxGeometry(0.9, 0.9, 0.6),
    b.indicator,
    [-3.4, 0, 3.4].map((x) => [x, 5.4, 6.7])
  ))
  b.add(instances(
    new THREE.BoxGeometry(2.4, 0.6, 0.5),
    b.panel,
    [-3.4, 3.4].map((z) => [6.7, -3.6, z])
  ))

  return bus
}

// Drone: quadcopter frame with four rotors, skids and a camera gimbal.
function drone(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(13, 16, 5.4, 1.8), b.carbon))
  const canopy = b.add(new THREE.Mesh(
    new THREE.SphereGeometry(6.4, 22, 14, 0, Math.PI * 2, 0, Math.PI / 2),
    b.plastic
  ))
  canopy.position.y = 2.4
  canopy.scale.set(1, 0.72, 1.25)

  const corners = [[-1, -1], [-1, 1], [1, -1], [1, 1]]
  corners.forEach(([sx, sz]) => {
    const angle = Math.atan2(sz, sx)
    const arm = b.add(new THREE.Mesh(roundedBoxGeometry(17, 2.3, 1.1, 0.5), b.carbon))
    arm.position.set(sx * 6.2, -0.5, sz * 6.2)
    arm.rotation.y = -angle
    const motor = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2.1, 2.3, 3.2, 16), b.accent))
    motor.position.set(sx * 12.6, 1.1, sz * 12.6)
    const hub = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 1.6, 10), b.metal))
    hub.position.set(sx * 12.6, 3.4, sz * 12.6)
    const blade = b.add(new THREE.Mesh(roundedBoxGeometry(14, 1.35, 0.2, 0.5), b.carbon))
    blade.position.set(sx * 12.6, 4.15, sz * 12.6)
    blade.rotation.y = (sx === sz ? 1 : -1) * Math.PI / 8
    ;[-1, 1].forEach((tipSide) => {
      const bladeTip = b.add(new THREE.Mesh(roundedBoxGeometry(1.8, 1.45, 0.24, 0.45), b.accent))
      bladeTip.position.set(
        sx * 12.6 + Math.cos(blade.rotation.y) * tipSide * 6.1,
        4.18,
        sz * 12.6 - Math.sin(blade.rotation.y) * tipSide * 6.1
      )
      bladeTip.rotation.y = blade.rotation.y
    })
  })

  b.add(instances(
    new THREE.BoxGeometry(1.2, 1.1, 16),
    b.carbon,
    [-4.6, 4.6].map((x) => [x, -5.6, 0])
  ))
  b.add(instances(
    new THREE.BoxGeometry(1, 3.6, 1),
    b.rubber,
    [-4.6, 4.6].flatMap((x) => [-4.6, 4.6].map((z) => [x, -4, z]))
  ))

  const gimbal = b.add(new THREE.Mesh(new THREE.SphereGeometry(2.6, 14, 12), b.metal))
  gimbal.position.set(0, -3.6, 4.4)
  const lens = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.3, 1.3, 1.6, 14), b.glass))
  lens.position.set(0, -3.6, 6.6)
  lens.rotation.x = Math.PI / 2
  const tail = b.add(new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.4, 0.6), b.indicator))
  tail.position.set(0, 1.4, -8.4)

  return body
}

// Terminal: workstation with keyboard deck and a tilted display.
function terminal(b) {
  const base = b.add(new THREE.Mesh(roundedBoxGeometry(30, 18, 1.6, 1), b.metal))
  base.position.y = -11
  const deck = b.add(new THREE.Mesh(roundedBoxGeometry(27, 11, 1.2, 0.8), b.body))
  deck.position.set(0, -9.8, 2.6)

  const keys = []
  for (let row = 0; row < 5; row++) {
    for (let column = 0; column < 13; column++) {
      keys.push([-11.4 + column * 1.9, -9.1, -0.4 + row * 1.5])
    }
  }
  b.add(instances(new THREE.BoxGeometry(1.5, 0.5, 1.2), b.plastic, keys))
  const pad = b.add(new THREE.Mesh(roundedBoxGeometry(5, 5, 0.5, 0.5), b.plastic))
  pad.position.set(11, -9.1, 3.4)

  const stand = b.add(new THREE.Mesh(roundedBoxGeometry(5, 4, 8, 0.8), b.metal))
  stand.position.set(0, -6.4, -4.4)
  const hinge = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 9, 12), b.accent))
  hinge.position.set(0, -2.6, -4.4)
  hinge.rotation.z = Math.PI / 2

  const screen = new THREE.Group()
  screen.position.set(0, 2.2, -4.4)
  screen.rotation.x = -0.14
  const bezel = b.add(new THREE.Mesh(roundedBoxGeometry(28, 1.8, 18, 1.4), b.plastic))
  const glass = b.add(new THREE.Mesh(roundedBoxGeometry(25, 0.7, 15, 1), b.screen))
  glass.position.z = 1.2
  const camera = b.add(new THREE.Mesh(new THREE.SphereGeometry(0.6, 10, 8), b.cavity))
  camera.position.set(0, 8.6, 1.2)
  const screenLines = b.add(instances(
    new THREE.BoxGeometry(9, 0.42, 0.18),
    b.screenInk,
    [[-5.8, 4.8, 1.64], [3.8, 1.8, 1.64], [-2.6, -1.2, 1.64], [4.8, -4.2, 1.64]]
  ))
  const power = b.add(new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.7, 0.5), b.indicator))
  power.position.set(-11.5, -7.6, 1.2)
  screen.add(bezel, glass, camera, screenLines, power)
  b.group.add(screen)

  return base
}

// Gateway: upright appliance with a shield front plate and a status column.
function gateway(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(22, 19, 30, 2), b.body))
  const bezel = b.add(new THREE.Mesh(roundedBoxGeometry(17, 1.4, 25, 1.2), b.panel))
  bezel.position.set(0, 0.6, 9.6)

  const shield = b.add(new THREE.Mesh(shieldGeometry(11, 14, 1.2), b.accent))
  shield.position.set(0, 1.6, 10.2)

  b.add(instances(
    new THREE.BoxGeometry(1.5, 1.5, 0.6),
    b.indicator,
    [-7, -7, -7, -7, -7].map((x, index) => [x, -10 + index * 2.6, 10.5])
  ))
  b.add(instances(
    new THREE.BoxGeometry(8, 0.5, 0.5),
    b.cavity,
    Array.from({ length: 5 }, (_, i) => [5.4, -9 + i * 2.4, 10.5])
  ))
  b.add(instances(
    new THREE.BoxGeometry(0.5, 9, 0.6),
    b.cavity,
    [-4, 0, 4].flatMap((z) => [[11.2, 2, z]])
  ))

  const foot = b.add(new THREE.Mesh(roundedBoxGeometry(24, 21, 1.6, 1), b.panel))
  foot.position.y = -15.8
  b.add(instances(
    new THREE.CylinderGeometry(1, 1, 1, 10),
    b.panel,
    [-9, 9].flatMap((x) => [-8, 8].map((z) => [x, -15.8, z]))
  ))

  ;[-6.4, 6.4].forEach((x, index) => {
    const antenna = new THREE.Group()
    antenna.position.set(x, 13.4, -7.4)
    antenna.rotation.set(-0.24, 0, index ? -0.16 : 0.16)
    const rod = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 11, 10), b.panel))
    rod.position.y = 5.5
    const cap = b.add(new THREE.Mesh(new THREE.SphereGeometry(1.05, 10, 8), b.accent))
    cap.position.y = 11.4
    antenna.add(rod, cap)
    b.group.add(antenna)
  })

  return body
}

// Service: slide-in service module standing in its bay.
function service(b) {
  // Open rack frame with three service units inside.
  const base = b.add(new THREE.Mesh(roundedBoxGeometry(30, 22, 2.4, 1), b.metal))
  base.position.y = -15.4
  const top = b.add(new THREE.Mesh(roundedBoxGeometry(30, 22, 1.6, 1), b.metal))
  top.position.y = 15.6
  b.add(instances(
    new THREE.BoxGeometry(2, 30, 2),
    b.metal,
    [[-13.4, 0, -9.4], [13.4, 0, -9.4], [-13.4, 0, 9.4], [13.4, 0, 9.4]]
  ))

  const unitY = [8.6, 0, -8.6]
  b.add(instances(new THREE.BoxGeometry(25, 5.4, 17), b.body, unitY.map((y) => [0, y, 0])))
  b.add(instances(new THREE.BoxGeometry(22, 4.2, 1.2), b.panel, unitY.map((y) => [0, y, 8.7])))
  b.add(instances(
    new THREE.BoxGeometry(1.2, 1.2, 0.6),
    b.indicator,
    unitY.flatMap((y) => [4.4, 6.4, 8.4].map((x) => [x, y, 9.4]))
  ))
  b.add(instances(
    new THREE.BoxGeometry(4.6, 1.2, 1.4),
    b.accent,
    unitY.flatMap((y) => [[-8.6, y, 9.1]])
  ))
  b.add(instances(
    new THREE.BoxGeometry(0.7, 3, 0.5),
    b.cavity,
    unitY.flatMap((y) => Array.from({ length: 5 }, (_, i) => [-4.6 + i * 1.6, y, 9.4]))
  ))

  return base
}

// Storage: array tower with four hot-swap drive trays.
function storage(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(20, 24, 32, 2), b.body))
  const bezel = b.add(new THREE.Mesh(roundedBoxGeometry(17, 1.4, 28, 1.2), b.panel))
  bezel.position.set(0, 0, 12.4)

  const trayY = [-10.8, -3.6, 3.6, 10.8]
  b.add(instances(new THREE.BoxGeometry(16, 2.6, 5.4), b.panel, trayY.map((y) => [0, y, 13.4])))
  b.add(instances(new THREE.BoxGeometry(14, 1, 1.2), b.accent, trayY.map((y) => [0, y - 1.6, 15.8])))
  b.add(instances(new THREE.BoxGeometry(1.1, 1.1, 0.5), b.indicator, trayY.map((y) => [6.4, y + 0.6, 16.2])))

  const cap = b.add(new THREE.Mesh(roundedBoxGeometry(17, 15, 1.6, 1), b.panel))
  cap.position.y = 16.2
  const ring = b.add(new THREE.Mesh(new THREE.TorusGeometry(4.6, 0.5, 10, 28), b.accent))
  ring.position.y = 17.4
  ring.rotation.x = Math.PI / 2

  b.add(instances(
    new THREE.BoxGeometry(0.4, 16, 0.8),
    b.cavity,
    Array.from({ length: 6 }, (_, i) => [-10.2, -0.4, -6 + i * 2.4])
  ))

  const foot = b.add(new THREE.Mesh(roundedBoxGeometry(21, 25, 2, 1), b.panel))
  foot.position.y = -16.4

  return body
}

// Controller: console with dials, a joystick and a display panel.
function controller(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(28, 20, 11, 2), b.body))
  const deck = b.add(new THREE.Mesh(roundedBoxGeometry(23, 15, 1.4, 1), b.panel))
  deck.position.y = 5.9

  ;[-7.6, 0].forEach((x) => {
    const dial = b.add(new THREE.Mesh(new THREE.TorusGeometry(3.2, 0.7, 10, 26), b.accent))
    dial.position.set(x, 7, -2.6)
    dial.rotation.x = Math.PI / 2
    const knob = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2.1, 2.1, 1.6, 18), b.body))
    knob.position.set(x, 7.3, -2.6)
  })

  const stick = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 8, 12), b.panel))
  stick.position.set(8, 9.6, 2.4)
  stick.rotation.z = -0.2
  const grip = b.add(new THREE.Mesh(new THREE.SphereGeometry(1.9, 14, 12), b.accent))
  grip.position.set(7.2, 13.4, 2.4)

  const bezel = b.add(new THREE.Mesh(roundedBoxGeometry(21, 1.2, 8, 1), b.panel))
  bezel.position.set(0, 0.4, 10.2)
  const display = b.add(new THREE.Mesh(roundedBoxGeometry(17, 0.8, 5.4, 0.6), b.screen))
  display.position.set(0, 0.6, 10.8)
  b.add(instances(
    new THREE.BoxGeometry(3.2, 0.34, 0.18),
    b.screenInk,
    [[-5.2, 1.7, 11.25], [-1.2, 1.7, 11.25], [3.1, 1.7, 11.25], [5.2, -0.5, 11.25]]
  ))
  b.add(instances(
    new THREE.BoxGeometry(1.4, 1.4, 0.6),
    b.indicator,
    [[-10, -3.6, 10.9], [-7.4, -3.6, 10.9]]
  ))
  b.add(instances(
    new THREE.BoxGeometry(0.6, 0.6, 0.5),
    b.cavity,
    [[8, -3.6, 10.9], [9.8, -3.6, 10.9], [11.6, -3.6, 10.9]]
  ))

  const foot = b.add(new THREE.Mesh(roundedBoxGeometry(29, 21, 1.8, 1), b.panel))
  foot.position.y = -6.2

  return body
}

// Sensor: pole-mounted dome with a status ring, lens and stub antennae.
function sensor(b) {
  const mount = b.add(new THREE.Mesh(roundedBoxGeometry(14, 14, 2, 1), b.concrete))
  mount.position.y = -15.4
  b.add(instances(
    new THREE.CylinderGeometry(0.7, 0.7, 1.4, 10),
    b.accent,
    [-5, 5].flatMap((x) => [-5, 5].map((z) => [x, -15.4, z]))
  ))

  const stem = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.5, 9, 14), b.metal))
  stem.position.y = -9.6

  const dome = b.add(new THREE.Mesh(
    new THREE.SphereGeometry(6.6, 24, 14, 0, Math.PI * 2, 0, Math.PI / 2),
    b.shell
  ))
  dome.position.y = -5.2
  const rim = b.add(new THREE.Mesh(new THREE.CylinderGeometry(6.7, 6.7, 0.8, 26), b.metal))
  rim.position.y = -5.4
  const ring = b.add(new THREE.Mesh(new THREE.TorusGeometry(6.7, 0.42, 10, 32), b.indicator))
  ring.position.y = -4.6
  ring.rotation.x = Math.PI / 2

  const eyeRim = b.add(new THREE.Mesh(new THREE.TorusGeometry(2.3, 0.5, 10, 24), b.accent))
  eyeRim.position.set(0, 0.6, 5.6)
  const eye = b.add(new THREE.Mesh(new THREE.SphereGeometry(1.9, 14, 12), b.glass))
  eye.position.set(0, 0.6, 5.4)

  ;[-1, 1].forEach((side) => {
    const antenna = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 10, 8), b.rubber))
    antenna.position.set(side * 5.4, 1.6, -2.6)
    antenna.rotation.set(0.3, 0, side * 0.55)
    const tip = b.add(new THREE.Mesh(new THREE.SphereGeometry(0.8, 10, 8), b.accent))
    tip.position.set(side * 7.9, 5.9, -4.1)
  })

  return dome
}

// Actuator: geared motor with cooling fins, flange, shaft and output arm.
function actuator(b) {
  const body = b.add(new THREE.Mesh(new THREE.CylinderGeometry(6, 6, 16, 26), b.body))
  b.add(instances(new THREE.BoxGeometry(1, 15, 2.6), b.panel, around(16, 6.1, 0)))

  const flange = b.add(new THREE.Mesh(new THREE.CylinderGeometry(8.6, 8.6, 2, 26), b.metal))
  flange.position.y = -9.4
  b.add(instances(new THREE.CylinderGeometry(0.9, 0.9, 1.4, 10), b.accent, around(4, 7, -8.4, Math.PI / 4)))

  const cap = b.add(new THREE.Mesh(new THREE.CylinderGeometry(6.2, 6.2, 1.6, 26), b.metal))
  cap.position.y = 8.5
  const band = b.add(new THREE.Mesh(new THREE.TorusGeometry(6.35, 0.45, 10, 32), b.indicator))
  band.position.y = 4.6
  band.rotation.x = Math.PI / 2

  const shaft = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.7, 1.7, 8, 14), b.metal))
  shaft.position.y = 12.4
  const hub = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2.5, 2.5, 2.2, 16), b.panel))
  hub.position.y = 16
  const arm = b.add(new THREE.Mesh(roundedBoxGeometry(16, 3, 1.6, 0.7), b.body))
  arm.position.set(5.2, 16, 0)
  arm.rotation.z = 0.1
  const joint = b.add(new THREE.Mesh(new THREE.SphereGeometry(1.8, 12, 10), b.accent))
  joint.position.set(12.6, 16.6, 0)
  const link = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 9, 10), b.metal))
  link.position.set(15.4, 12.4, 0)
  link.rotation.z = -0.52
  const bracket = b.add(new THREE.Mesh(roundedBoxGeometry(4.4, 4, 2.6, 0.5), b.panel))
  bracket.position.set(17.6, 8.4, 0)

  return body
}

// Fallback for categories without a dedicated builder.
function generic(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(26, 22, 20, 3), b.body))
  const rail = b.add(new THREE.Mesh(roundedBoxGeometry(27, 23, 1.6, 2), b.panel))
  rail.position.y = 8
  const ring = b.add(new THREE.Mesh(new THREE.TorusGeometry(9, 0.8, 10, 32), b.accent))
  ring.position.y = 10.5
  ring.rotation.x = Math.PI / 2
  const led = b.add(new THREE.Mesh(new THREE.SphereGeometry(1.7, 12, 10), b.indicator))
  led.position.set(0, -6, 11.4)
  return body
}

// Router variants -------------------------------------------------------------

// 立式三频路由器：竖立机身、前置指示灯柱、顶部四根天线。
function routerTower(b) {
  const width = b.jitter(23)
  const height = b.jitter(34)
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(width, 19, height, 2), b.body))
  b.add(new THREE.Mesh(roundedBoxGeometry(width - 4, 1.2, height - 5, 1.2), b.panel)).position.set(0, 0, 9.7)

  b.add(instances(
    new THREE.BoxGeometry(1.3, 2.6, 0.6),
    b.indicator,
    Array.from({ length: 6 }, (_, i) => [-width / 2 + 3.2, 10 - i * 4, 10.5])
  ))
  b.add(instances(
    new THREE.BoxGeometry(8, 0.5, 0.5),
    b.cavity,
    Array.from({ length: 6 }, (_, i) => [2.4, 6 - i * 2.4, 10.5])
  ))
  b.add(instances(
    new THREE.BoxGeometry(3.2, 2.6, 1),
    b.port,
    [-6, 0, 6].map((x) => [x, -height / 2 + 4, 10.3])
  ))
  const badge = b.add(new THREE.Mesh(new THREE.TorusGeometry(2.6, 0.4, 10, 26), b.accent))
  badge.position.set(0, height / 2 - 6, 10.4)

  const base = b.add(new THREE.Mesh(roundedBoxGeometry(width + 5, 22, 2.2, 1), b.panel))
  base.position.y = -height / 2 - 1
  b.add(instances(
    new THREE.CylinderGeometry(1.1, 1.1, 0.9, 12),
    b.panel,
    [-1, 1].flatMap((sx) => [-1, 1].map((sz) => [sx * (width / 2 + 1), -height / 2 - 2.4, sz * 9]))
  ))

  ;[-1, 1].forEach((sx) => {
    [-1, 1].forEach((sz) => {
      const antenna = new THREE.Group()
      antenna.position.set(sx * (width / 2 - 2.4), height / 2 - 1, sz * 5.4)
      antenna.rotation.set(sz * 0.22, 0, sx * 0.26)
      const rod = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 15, 10), b.panel))
      rod.position.y = 7.5
      const tip = b.add(new THREE.Mesh(new THREE.SphereGeometry(1, 10, 8), b.accent))
      tip.position.y = 15.4
      antenna.add(rod, tip)
      b.group.add(antenna)
    })
  })
  return body
}

// 机架式 1U 路由器：扁机箱、上行光口、双电源、导轨耳。
function routerRack(b) {
  const width = b.jitter(40)
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(width, 16, 5.4, 1.4), b.body))
  b.add(new THREE.Mesh(roundedBoxGeometry(width - 3, 1.2, 4.2, 0.8), b.panel)).position.set(0, 0, 8.4)
  b.add(instances(
    new THREE.BoxGeometry(5.4, 3.4, 1),
    b.port,
    [-13.4, -7.6, -1.8, 4].map((x) => [x, 0, 8.9])
  ))
  b.add(instances(
    new THREE.BoxGeometry(3.6, 1.2, 0.4),
    b.cavity,
    [-13.4, -7.6, -1.8, 4].map((x) => [x, -0.6, 9.4])
  ))
  b.add(instances(
    new THREE.BoxGeometry(3.4, 2.4, 1.2),
    b.cavity,
    [10.6, 15].map((x) => [x, 0, 8.9])
  ))
  b.add(instances(
    new THREE.BoxGeometry(1.2, 1.2, 0.6),
    b.indicator,
    [-18.2, -18.2, -18.2, -18.2].map((x, i) => [x, 4.4 - i * 2.4, 9])
  ))
  b.add(instances(
    new THREE.BoxGeometry(5, 4.6, 1),
    b.panel,
    [18.4, 18.4].map((x, i) => [x, i ? -2.6 : 2.6, 8.6])
  ))
  ;[-width / 2 - 1.4, width / 2 + 1.4].forEach((x) => {
    b.add(new THREE.Mesh(roundedBoxGeometry(3, 12, 5.6, 0.6), b.panel)).position.set(x, 0, 0)
  })
  b.add(instances(
    new THREE.BoxGeometry(9, 0.4, 0.5),
    b.panel,
    Array.from({ length: 5 }, (_, i) => [0, 2.6, -5 + i * 2.5])
  ))
  return body
}

// 便携路由器：小机身、单天线、挂绳环。
function routerPortable(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(b.jitter(21), 15, 6.6, 1.6), b.body))
  b.add(new THREE.Mesh(roundedBoxGeometry(16, 10, 0.9, 1), b.panel)).position.y = 3.5
  b.add(instances(
    new THREE.BoxGeometry(3.4, 2.6, 1),
    b.port,
    [-4.2, 4.2].map((x) => [x, -1, 8.1])
  ))
  b.add(instances(
    new THREE.BoxGeometry(0.9, 0.9, 0.5),
    b.indicator,
    [-3, 0, 3].map((x) => [x, 2.2, 7.8])
  ))
  const ring = b.add(new THREE.Mesh(new THREE.TorusGeometry(1.6, 0.38, 8, 20), b.accent))
  ring.position.set(-11.4, 0, 0)
  ring.rotation.y = Math.PI / 2
  const antenna = new THREE.Group()
  antenna.position.set(6.4, 3.2, -4.4)
  antenna.rotation.set(-0.32, 0, 0.34)
  const rod = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 13, 10), b.panel))
  rod.position.y = 6.5
  const tip = b.add(new THREE.Mesh(new THREE.SphereGeometry(1, 10, 8), b.accent))
  tip.position.y = 13.2
  antenna.add(rod, tip)
  b.group.add(antenna)
  b.add(instances(
    new THREE.CylinderGeometry(0.9, 0.9, 0.8, 10),
    b.panel,
    [-6, 6].flatMap((x) => [-4.4, 4.4].map((z) => [x, -3.8, z]))
  ))
  return body
}

// Switch variants -------------------------------------------------------------

// 桌面型 8 口交换机：无导轨耳，单排端口。
function switchDesktop(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(b.jitter(27), 16, 5, 1.4), b.body))
  b.add(new THREE.Mesh(roundedBoxGeometry(23, 12, 0.8, 1), b.panel)).position.y = 2.8
  const columns = Array.from({ length: 8 }, (_, i) => -9.8 + i * 2.8)
  b.add(instances(new THREE.BoxGeometry(2.4, 2.4, 0.9), b.port, columns.map((x) => [x, -0.4, 8.2])))
  b.add(instances(new THREE.BoxGeometry(1.6, 1.6, 0.3), b.cavity, columns.map((x) => [x, -0.4, 8.7])))
  b.add(instances(new THREE.BoxGeometry(1.2, 1.2, 0.5), b.indicator, columns.map((x) => [x, -2.4, 8.2])))
  const link = b.add(new THREE.Mesh(roundedBoxGeometry(4.6, 0.8, 2.6, 0.4), b.accent))
  link.position.set(11.6, 0.6, 8)
  b.add(instances(
    new THREE.CylinderGeometry(1, 1, 0.8, 10),
    b.panel,
    [-9, 9].flatMap((x) => [-5, 5].map((z) => [x, -3, z]))
  ))
  return body
}

// 16 口接入交换机：双排端口 + 两个上行光口。
function switchUplink(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(b.jitter(34), 18, 6.2, 1.6), b.body))
  b.add(new THREE.Mesh(roundedBoxGeometry(30, 14, 0.9, 1), b.panel)).position.y = 3.4
  const columns = Array.from({ length: 8 }, (_, i) => -11.4 + i * 3.2)
  const rows = [-1.6, 1.6]
  const ports = rows.flatMap((y) => columns.map((x) => [x, y, 8.9]))
  b.add(instances(new THREE.BoxGeometry(2.6, 2.6, 0.9), b.port, ports))
  b.add(instances(new THREE.BoxGeometry(1.7, 1.7, 0.3), b.cavity, ports.map(([x, y]) => [x, y, 9.4])))
  b.add(instances(
    new THREE.BoxGeometry(3, 3.2, 1.2),
    b.cavity,
    [[16.4, 1.8, 8.9], [16.4, -1.8, 8.9]]
  ))
  b.add(instances(
    new THREE.BoxGeometry(1.1, 1.1, 0.6),
    b.indicator,
    Array.from({ length: 4 }, (_, i) => [-16.6, 2.4 - i * 1.6, 8.9])
  ))
  ;[-19.4, 19.4].forEach((x) => {
    b.add(new THREE.Mesh(roundedBoxGeometry(3.4, 13, 6.6, 0.8), b.panel)).position.set(x, 0, 0)
  })
  return body
}

// Terminal variants -----------------------------------------------------------

// 笔记本终端：掀开的屏幕 + 键盘 + 触控板。
function terminalLaptop(b) {
  const width = b.jitter(28)
  const base = b.add(new THREE.Mesh(roundedBoxGeometry(width, 19, 1.6, 1), b.body))
  base.position.y = -9.5
  const deck = b.add(new THREE.Mesh(roundedBoxGeometry(width - 3, 12, 1.1, 0.8), b.panel))
  deck.position.set(0, -8.6, 2.6)
  const keys = []
  for (let row = 0; row < 5; row++) {
    for (let column = 0; column < 12; column++) keys.push([-11 + column * 2, -8.1, row * 1.5])
  }
  b.add(instances(new THREE.BoxGeometry(1.5, 0.5, 1.2), b.port, keys))
  b.add(new THREE.Mesh(roundedBoxGeometry(7, 4.4, 0.5, 0.5), b.port)).position.set(0, -8.1, 7.6)

  const hinge = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, width - 6, 12), b.accent))
  hinge.position.set(0, -8.6, -8.6)
  hinge.rotation.z = Math.PI / 2

  const screen = new THREE.Group()
  screen.position.set(0, -8.6, -8.4)
  screen.rotation.x = -1.85
  const lid = b.add(new THREE.Mesh(roundedBoxGeometry(width, 1.4, 18, 1), b.body))
  lid.position.y = 9
  const glass = b.add(new THREE.Mesh(roundedBoxGeometry(width - 4, 0.7, 15, 1), b.glass))
  glass.position.set(0, 9.4, 0.9)
  const power = b.add(new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.6, 0.5), b.indicator))
  power.position.set(-8, 3.6, 0.9)
  screen.add(lid, glass, power)
  b.group.add(screen)
  return base
}

// 双屏工作站：两台显示器 + 键盘 + 主机塔。
function terminalDual(b) {
  const desk = b.add(new THREE.Mesh(roundedBoxGeometry(b.jitter(34), 15, 1.4, 1), b.panel))
  desk.position.y = -11
  const keyboard = b.add(new THREE.Mesh(roundedBoxGeometry(24, 9, 1.2, 0.8), b.body))
  keyboard.position.set(0, -10, 6)
  const keys = []
  for (let row = 0; row < 4; row++) {
    for (let column = 0; column < 13; column++) keys.push([-11.4 + column * 1.9, -9.3, 2.4 + row * 1.5])
  }
  b.add(instances(new THREE.BoxGeometry(1.5, 0.5, 1.2), b.port, keys))

  const tower = b.add(new THREE.Mesh(roundedBoxGeometry(10, 12, 20, 1.2), b.body))
  tower.position.set(17.6, -1, 0)
  b.add(instances(
    new THREE.BoxGeometry(0.6, 8, 0.6),
    b.cavity,
    [[17.6, 3, 6.2], [17.6, -2, 6.2]]
  ))

  ;[-1, 1].forEach((side) => {
    const screen = new THREE.Group()
    screen.position.set(side * 10.6, -2.4, -3.4)
    screen.rotation.y = -side * 0.34
    const bezel = b.add(new THREE.Mesh(roundedBoxGeometry(19, 1.6, 14, 1.2), b.body))
    bezel.position.y = 7
    const glass = b.add(new THREE.Mesh(roundedBoxGeometry(16.5, 0.7, 11.5, 1), b.glass))
    glass.position.set(0, 7.2, 1)
    const stem = b.add(new THREE.Mesh(roundedBoxGeometry(3, 3, 7, 0.6), b.panel))
    const foot = b.add(new THREE.Mesh(roundedBoxGeometry(9, 7, 1, 0.6), b.panel))
    foot.position.y = -3.6
    const power = b.add(new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.6, 0.5), b.indicator))
    power.position.set(-7, 1.6, 1)
    screen.add(bezel, glass, stem, foot, power)
    b.group.add(screen)
  })
  return desk
}

// Drone variants --------------------------------------------------------------

// 六旋翼：六个机臂环状分布。
function droneHexa(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(15, 18, 5.6, 1.8), b.body))
  const canopy = b.add(new THREE.Mesh(
    new THREE.SphereGeometry(7, 22, 14, 0, Math.PI * 2, 0, Math.PI / 2),
    b.panel
  ))
  canopy.position.y = 2.4
  canopy.scale.set(1, 0.7, 1.2)

  around(6, 1, 0).forEach((hit) => {
    const angle = Math.atan2(hit[2], hit[0])
    const arm = b.add(new THREE.Mesh(roundedBoxGeometry(15, 2.2, 1.1, 0.5), b.panel))
    arm.position.set(Math.cos(angle) * 6.4, -0.5, Math.sin(angle) * 6.4)
    arm.rotation.y = -angle
    const motor = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2, 2.2, 3.2, 14), b.accent))
    motor.position.set(Math.cos(angle) * 12.4, 1.1, Math.sin(angle) * 12.4)
    const rotor = b.add(new THREE.Mesh(new THREE.CylinderGeometry(6.4, 6.4, 0.24, 22), b.glass))
    rotor.position.set(Math.cos(angle) * 12.4, 3.1, Math.sin(angle) * 12.4)
    const hub = b.add(new THREE.Mesh(new THREE.SphereGeometry(1, 10, 8), b.indicator))
    hub.position.set(Math.cos(angle) * 12.4, 3.5, Math.sin(angle) * 12.4)
  })
  b.add(instances(
    new THREE.BoxGeometry(1.2, 1.1, 15),
    b.panel,
    [-5, 5].map((x) => [x, -5.6, 0])
  ))
  b.add(instances(
    new THREE.BoxGeometry(1, 3.6, 1),
    b.panel,
    [-5, 5].flatMap((x) => [-4.6, 4.6].map((z) => [x, -4, z]))
  ))
  const gimbal = b.add(new THREE.Mesh(new THREE.SphereGeometry(2.6, 14, 12), b.panel))
  gimbal.position.set(0, -4, 4.6)
  const lens = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.3, 1.3, 1.6, 14), b.glass))
  lens.position.set(0, -4, 6.8)
  lens.rotation.x = Math.PI / 2
  return body
}

// 固定翼无人机：机身 + 主翼 + 尾翼 + 螺旋桨。
function droneFixedWing(b) {
  const fuselage = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2.8, 1.7, 24, 16), b.body))
  fuselage.rotation.x = Math.PI / 2
  const nose = b.add(new THREE.Mesh(new THREE.ConeGeometry(2.8, 5, 16), b.panel))
  nose.position.z = 14
  nose.rotation.x = Math.PI / 2
  b.add(new THREE.Mesh(roundedBoxGeometry(34, 5, 1, 0.5), b.body))
  ;[-1, 1].forEach((side) => {
    const tip = b.add(new THREE.Mesh(roundedBoxGeometry(2.4, 4, 1.4, 0.5), b.accent))
    tip.position.set(side * 16.4, 0.8, 0)
  })
  const fin = b.add(new THREE.Mesh(roundedBoxGeometry(1, 4.6, 7, 0.6), b.body))
  fin.position.set(0, 4.4, -11)
  const stabilizer = b.add(new THREE.Mesh(roundedBoxGeometry(13, 3.6, 1, 0.5), b.body))
  stabilizer.position.set(0, 1.6, -11.5)
  const hub = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 1.6, 12), b.accent))
  hub.position.z = 16.4
  hub.rotation.x = Math.PI / 2
  b.add(instances(new THREE.BoxGeometry(0.6, 13, 0.3), b.panel, [[0, 0, 16.6]]))
  b.add(instances(new THREE.BoxGeometry(0.6, 13, 0.3), b.panel, [[0, 0, 16.6, 0.9, 0, 0]]))
  const pod = b.add(new THREE.Mesh(new THREE.SphereGeometry(2.4, 14, 12), b.glass))
  pod.position.set(0, -2.6, 4)
  const tail = b.add(new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.4, 0.6), b.indicator))
  tail.position.set(0, 6.9, -11)
  return fuselage
}

// Station / satellite / gateway / service / storage / controller / sensor /
// actuator variants -----------------------------------------------------------

// 一体化单扇区杆站：立杆 + 单面天线板 + 天线罩 + 设备柜。
function stationPole(b) {
  const pad = b.add(new THREE.Mesh(roundedBoxGeometry(22, 20, 2.2, 0.8), b.panel))
  pad.position.y = -17
  const mast = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.4, 1.9, 28, 14), b.panel))
  mast.position.set(5, -2, 0)
  const collar = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.2, 1.4, 14), b.accent))
  collar.position.set(5, 6.4, 0)
  const panel = b.add(new THREE.Mesh(roundedBoxGeometry(6, 2, 15, 1), b.body))
  panel.position.set(11.4, 15, 0)
  panel.rotation.y = -Math.PI / 2
  const brace = b.add(new THREE.Mesh(roundedBoxGeometry(7, 1, 1, 0.4), b.panel))
  brace.position.set(8.2, 15, 0)
  const radome = b.add(new THREE.Mesh(
    new THREE.SphereGeometry(4.4, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2),
    b.shell
  ))
  radome.position.set(-7, 8, 0)
  const cabinet = b.add(new THREE.Mesh(roundedBoxGeometry(11, 9, 9, 1), b.body))
  cabinet.position.set(-7, -11, 0)
  b.add(instances(
    new THREE.BoxGeometry(1.4, 1.4, 0.6),
    b.indicator,
    [[-2.6, -8.4, 4.8], [-2.6, -11.6, 4.8]]
  ))
  const beacon = b.add(new THREE.Mesh(new THREE.SphereGeometry(1.2, 12, 10), b.indicator))
  beacon.position.set(5, 13.6, 0)
  return pad
}

// 立方星：小箱体 + 四片小帆板 + 鞭状天线 + 相机。
function satelliteCube(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(12, 12, 12, 1), b.body))
  b.add(new THREE.Mesh(roundedBoxGeometry(12.6, 12.6, 1.2, 0.5), b.panel)).position.y = 3.4
  ;[-1, 1].forEach((side) => {
    [-1, 1].forEach((level) => {
      const boom = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 5, 8), b.accent))
      boom.position.set(side * 8, level * 3.4, 0)
      boom.rotation.z = Math.PI / 2
      const wing = b.add(new THREE.Mesh(roundedBoxGeometry(9, 8, 0.7, 0.4), b.shell))
      wing.position.set(side * 15, level * 3.4, 0)
      const frame = b.add(new THREE.Mesh(roundedBoxGeometry(9.4, 1, 1, 0.4), b.accent))
      frame.position.set(side * 15, level * 3.4, 0)
    })
  })
  const whip = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.35, 12, 8), b.panel))
  whip.position.set(0, 12, 0)
  const tip = b.add(new THREE.Mesh(new THREE.SphereGeometry(1, 10, 8), b.indicator))
  tip.position.set(0, 18.2, 0)
  const lens = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.8, 1.8, 1.6, 14), b.glass))
  lens.position.set(0, -2.4, 6.4)
  lens.rotation.x = Math.PI / 2
  b.add(instances(
    new THREE.BoxGeometry(0.8, 0.8, 0.5),
    b.indicator,
    [-3, 0, 3].map((x) => [x, 4.4, 6.4])
  ))
  return body
}

// 机架式防火墙：1U 扁机箱 + 管理屏 + RJ45/SFP 端口组。
function gatewayRack(b) {
  const width = 40
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(width, 16, 5.6, 1.4), b.body))
  const face = b.add(new THREE.Mesh(roundedBoxGeometry(width - 3.2, 1.2, 4.35, 0.65), b.panel))
  face.position.set(0, 0, 8.55)

  // Recessed monochrome management display and its live telemetry strokes.
  const displayBezel = b.add(new THREE.Mesh(roundedBoxGeometry(8.8, 0.8, 3.15, 0.45), b.cavity))
  displayBezel.position.set(-13.8, 0.15, 9.3)
  const display = b.add(new THREE.Mesh(roundedBoxGeometry(7.4, 0.5, 2.15, 0.3), b.screen))
  display.position.set(-13.8, 0.15, 9.82)
  b.add(instances(
    new THREE.BoxGeometry(1.25, 0.18, 0.12),
    b.screenInk,
    [[-16, 0.48, 10.14], [-14.3, 0.48, 10.14], [-12.1, 0.48, 10.14], [-14.9, -0.34, 10.14]]
  ))

  // Eight copper ports with dark socket cavities and link/activity indicators.
  const rj45 = Array.from({ length: 8 }, (_, i) => [-7.5 + i * 3.05, -0.2, 9.45])
  b.add(instances(new THREE.BoxGeometry(2.55, 2.65, 0.7), b.metal, rj45))
  b.add(instances(
    new THREE.BoxGeometry(1.95, 1.7, 0.55),
    b.port,
    rj45.map(([x, y, z]) => [x, y - 0.2, z + 0.58])
  ))
  b.add(instances(
    new THREE.BoxGeometry(0.42, 0.42, 0.28),
    b.indicator,
    rj45.flatMap(([x]) => [[x - 0.65, 1.52, 10.05], [x + 0.65, 1.52, 10.05]])
  ))

  // Two metal SFP cages plus console and USB service ports.
  b.add(instances(
    new THREE.BoxGeometry(3.5, 1.65, 0.75),
    b.metal,
    [[11.5, -2.02, 9.48], [15.65, -2.02, 9.48]]
  ))
  b.add(instances(
    new THREE.BoxGeometry(2.75, 0.95, 0.48),
    b.cavity,
    [[11.5, -2.02, 10.05], [15.65, -2.02, 10.05]]
  ))
  b.add(instances(
    new THREE.BoxGeometry(1.65, 0.72, 0.5),
    b.cavity,
    [[-17.2, -2.05, 9.95], [-14.7, -2.05, 9.95]]
  ))

  // Pressed-steel rack ears and top ventilation slots.
  ;[-width / 2 - 1.4, width / 2 + 1.4].forEach((x) => {
    b.add(new THREE.Mesh(roundedBoxGeometry(3, 12, 5.8, 0.55), b.metal)).position.set(x, 0, 0)
  })
  b.add(instances(
    new THREE.BoxGeometry(12, 0.18, 0.62),
    b.cavity,
    Array.from({ length: 7 }, (_, i) => [-4, 2.9, -6 + i * 1.8])
  ))
  return body
}

// 单刀片服务模块：竖立薄刀片 + 上下把手。
function serviceBlade(b) {
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(b.jitter(24), 5, 30, 1.4), b.body))
  const face = b.add(new THREE.Mesh(roundedBoxGeometry(19, 1.4, 25, 1), b.panel))
  face.position.set(0, 0, 3.2)
  b.add(instances(
    new THREE.BoxGeometry(15, 0.6, 0.5),
    b.cavity,
    Array.from({ length: 7 }, (_, i) => [0, -1 - i * 1.7, 4])
  ))
  b.add(instances(
    new THREE.BoxGeometry(1.2, 1.2, 0.6),
    b.indicator,
    [-7.4, -7.4, -7.4].map((x, i) => [x, 8.6 - i * 2.8, 4])
  ))
  b.add(instances(
    new THREE.BoxGeometry(3, 3, 1),
    b.cavity,
    [[7, -9.4, 4], [7, -5.6, 4]]
  ))
  ;[12.6, -12.6].forEach((y) => {
    b.add(new THREE.Mesh(roundedBoxGeometry(10, 1.2, 1.6, 0.5), b.accent)).position.set(0, y, 3.4)
  })
  const base = b.add(new THREE.Mesh(roundedBoxGeometry(28, 14, 2, 1), b.panel))
  base.position.y = -16
  b.add(instances(
    new THREE.BoxGeometry(2.4, 26, 11),
    b.panel,
    [[-13.4, 0, 0], [13.4, 0, 0]]
  ))
  return body
}

// 机架式存储：2U 机箱 + 12 个热插拔盘位。
function storageRack(b) {
  const width = 40
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(width, 17, 7, 1.4), b.body))
  const face = b.add(new THREE.Mesh(roundedBoxGeometry(width - 3, 1.4, 5.6, 0.75), b.panel))
  face.position.set(0, 0, 9.2)
  const bays = []
  for (let row = 0; row < 3; row++) {
    for (let column = 0; column < 4; column++) bays.push([-13 + column * 4.4, 2.6 - row * 2.6, 9.9])
  }
  b.add(instances(new THREE.BoxGeometry(4.05, 2.3, 0.48), b.cavity, bays))
  b.add(instances(new THREE.BoxGeometry(3.65, 1.95, 0.72), b.metal, bays.map(([x, y, z]) => [x, y, z + 0.42])))
  b.add(instances(new THREE.BoxGeometry(2.7, 0.42, 0.34), b.rubber, bays.map(([x, y]) => [x, y - 0.53, 10.58])))
  b.add(instances(new THREE.BoxGeometry(0.3, 1.15, 0.28), b.cavity, bays.map(([x, y]) => [x - 1.38, y, 10.62])))
  b.add(instances(
    new THREE.BoxGeometry(0.42, 0.42, 0.3),
    b.indicator,
    bays.map(([x, y]) => [x + 1.38, y + 0.72, 10.72])
  ))

  // Narrow operator strip with power, health and locate controls.
  const controlStrip = b.add(new THREE.Mesh(roundedBoxGeometry(4.2, 0.72, 5.7, 0.4), b.cavity))
  controlStrip.position.set(16.25, 0, 10)
  b.add(instances(
    new THREE.CylinderGeometry(0.48, 0.48, 0.28, 14),
    b.indicator,
    [1.75, 0.25, -1.25].map((y) => [16.25, y, 10.55, Math.PI / 2])
  ))
  ;[-width / 2 - 1.4, width / 2 + 1.4].forEach((x) => {
    b.add(new THREE.Mesh(roundedBoxGeometry(3, 13, 7.2, 0.55), b.metal)).position.set(x, 0, 0)
  })
  return body
}

// 机架式控制器：1U 机箱 + 显示屏 + 双旋钮 + 摇杆。
function controllerRack(b) {
  const width = 38
  const body = b.add(new THREE.Mesh(roundedBoxGeometry(width, 16, 6, 1.4), b.body))
  const face = b.add(new THREE.Mesh(roundedBoxGeometry(width - 3, 1.2, 4.6, 0.72), b.panel))
  face.position.set(0, 0, 8.6)
  const screenBezel = b.add(new THREE.Mesh(roundedBoxGeometry(15.5, 0.72, 4.55, 0.55), b.cavity))
  screenBezel.position.set(-9, 0.3, 9.35)
  const screen = b.add(new THREE.Mesh(roundedBoxGeometry(13.7, 0.48, 3.2, 0.4), b.screen))
  screen.position.set(-9, 0.3, 9.85)
  b.add(instances(
    new THREE.BoxGeometry(2.4, 0.18, 0.12),
    b.screenInk,
    [[-13.4, 1.1, 10.16], [-10.2, 1.1, 10.16], [-6.9, 1.1, 10.16], [-10.6, -0.05, 10.16], [-6.8, -0.85, 10.16]]
  ))

  ;[1.1, 6.2].forEach((x) => {
    const dial = b.add(new THREE.Mesh(new THREE.TorusGeometry(1.65, 0.34, 8, 20), b.metal))
    dial.position.set(x, 0.45, 9.36)
    const knob = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.05, 1, 18), b.rubber))
    knob.position.set(x, 0.45, 9.88)
    knob.rotation.x = Math.PI / 2
    const marker = b.add(new THREE.Mesh(new THREE.BoxGeometry(0.2, 1.05, 0.16), b.shell))
    marker.position.set(x, 1, 10.43)
  })

  const emergencyMaterial = b.material({ color: '#a91f22', roughness: 0.42, metalness: 0.05 })
  const emergencyCollar = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2.15, 2.15, 0.55, 22), b.metal))
  emergencyCollar.position.set(13.7, 0.45, 9.65)
  emergencyCollar.rotation.x = Math.PI / 2
  const emergency = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.65, 1.05, 22), emergencyMaterial))
  emergency.position.set(13.7, 0.45, 10.2)
  emergency.rotation.x = Math.PI / 2
  b.add(instances(
    new THREE.BoxGeometry(0.72, 0.72, 0.4),
    b.indicator,
    [0.2, 2, 3.8, 5.6, 7.4].map((x) => [x, -2, 9.95])
  ))
  ;[-width / 2 - 1.4, width / 2 + 1.4].forEach((x) => {
    b.add(new THREE.Mesh(roundedBoxGeometry(3, 13, 6.4, 0.55), b.metal)).position.set(x, 0, 0)
  })
  return body
}

// 气象观测站：立杆 + 风杯 + 风向标 + 雨量筒 + 设备箱。
function sensorWeather(b) {
  const base = b.add(new THREE.Mesh(roundedBoxGeometry(16, 16, 2.4, 1), b.panel))
  base.position.y = -16.4
  const mast = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1, 1.2, 30, 14), b.panel))
  mast.position.y = 0
  const hub = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1.2, 2.4, 12), b.accent))
  hub.position.y = 15.2
  around(3, 5.4, 15.2).forEach((hit) => {
    const angle = Math.atan2(hit[2], hit[0])
    const arm = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 5.4, 8), b.accent))
    arm.position.set(Math.cos(angle) * 2.7, 15.2, Math.sin(angle) * 2.7)
    arm.rotation.z = Math.PI / 2
    arm.rotation.y = -angle
    const cup = b.add(new THREE.Mesh(
      new THREE.SphereGeometry(1.7, 12, 10, 0, Math.PI * 2, 0, Math.PI / 2),
      b.body
    ))
    cup.position.set(Math.cos(angle) * 5.4, 15.2, Math.sin(angle) * 5.4)
    cup.scale.set(1, 0.8, 1)
  })
  const vane = b.add(new THREE.Mesh(roundedBoxGeometry(1, 4.4, 1, 0.4), b.accent))
  vane.position.set(0, 9.6, 4.4)
  const vaneArm = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 8.8, 8), b.panel))
  vaneArm.position.set(0, 9.6, 0)
  vaneArm.rotation.x = Math.PI / 2
  const gauge = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2.6, 2.6, 5, 18), b.shell))
  gauge.position.set(0, 5, 6.4)
  const funnel = b.add(new THREE.Mesh(new THREE.ConeGeometry(2.6, 1.8, 18), b.accent))
  funnel.position.set(0, 8.2, 6.4)
  const box = b.add(new THREE.Mesh(roundedBoxGeometry(8, 6, 10, 1), b.body))
  box.position.set(0, -8, 0)
  b.add(instances(
    new THREE.BoxGeometry(1.2, 1.2, 0.6),
    b.indicator,
    [[-2.4, -4.6, 3.4], [2.4, -4.6, 3.4]]
  ))
  return mast
}

// 直线执行器：电机 + 丝杆推杆 + 行程指示。
function actuatorLinear(b) {
  const base = b.add(new THREE.Mesh(roundedBoxGeometry(15, 11, 2.4, 1), b.panel))
  base.position.y = -13
  b.add(instances(
    new THREE.CylinderGeometry(0.9, 0.9, 1.6, 10),
    b.accent,
    [-5.4, 5.4].flatMap((x) => [-3.6, 3.6].map((z) => [x, -11.8, z]))
  ))
  const motor = b.add(new THREE.Mesh(new THREE.CylinderGeometry(4.4, 4.4, 10, 20), b.body))
  motor.position.y = -6
  b.add(instances(new THREE.BoxGeometry(0.9, 9, 2.2), b.panel, around(14, 4.5, -6)))
  const gearbox = b.add(new THREE.Mesh(new THREE.CylinderGeometry(5, 4.4, 4, 18), b.panel))
  gearbox.position.y = -1
  const band = b.add(new THREE.Mesh(new THREE.TorusGeometry(4.6, 0.42, 8, 28), b.indicator))
  band.position.y = -6
  band.rotation.x = Math.PI / 2
  const rod = b.add(new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.5, 22, 14), b.accent))
  rod.position.y = 11
  const collar = b.add(new THREE.Mesh(new THREE.CylinderGeometry(2.4, 2.4, 1.6, 16), b.panel))
  collar.position.y = 21
  const clevis = b.add(new THREE.Mesh(roundedBoxGeometry(4.4, 4.4, 2.6, 0.5), b.panel))
  clevis.position.y = 23.4
  const pin = b.add(new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 5, 10), b.accent))
  pin.position.y = 23.4
  pin.rotation.z = Math.PI / 2
  const scale = b.add(new THREE.Mesh(roundedBoxGeometry(1.6, 16, 0.5, 0.3), b.cavity))
  scale.position.set(4.4, 10, 0)
  return motor
}

// Procedural models, one per node category returned by the LLM contract.
const modelBuilders = {
  router: [router, routerTower, routerRack, routerPortable],
  switch: [accessSwitch, switchDesktop, switchUplink],
  station: [station, stationPole],
  satellite: [satellite, satelliteCube],
  drone: [drone, droneHexa, droneFixedWing],
  terminal: [terminal, terminalLaptop, terminalDual],
  gateway: [gatewayRack, gateway],
  service: [service, serviceBlade],
  storage: [storageRack, storage],
  controller: [controllerRack, controller],
  sensor: [sensor, sensorWeather],
  actuator: [actuator, actuatorLinear]
}

// Blender-exported GLBs are selected by category and node id. The procedural
// builders above remain the immediate fallback while a GLB is loading.
export const NODE_MODEL_CATEGORIES = Object.keys(modelBuilders)

export const NODE_MODEL_VARIANTS = Object.fromEntries(
  Object.entries(NODE_MODEL_ASSETS).map(([category, assets]) => [category, assets.length])
)

// 稳定伪随机数只供备选模型的程序化微细节使用。
function makeRandom(seed) {
  let state = 2166136261
  const text = String(seed)
  for (let index = 0; index < text.length; index++) {
    state ^= text.charCodeAt(index)
    state = Math.imul(state, 16777619)
  }
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    return state / 4294967296
  }
}

export function createNodeModel(category, visual, status, seed, variantIndex = 0) {
  // The variant index is deterministic for a node id, so a topology keeps the
  // same hardware mix across re-renders and status updates.
  const random = makeRandom(category || seed)
  const variants = modelBuilders[category] || [generic]
  const build = variants[Math.abs(variantIndex) % variants.length]
  const builder = new ModelBuilder(visual, status, random, category)
  const primary = build(builder)
  addCaseHardware(builder, category, primary)
  return { ...builder.texture(), primary }
}
